import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useMotionValue, animate } from "framer-motion";
import axios from 'axios';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const MobileCarousel = () => {
    const [activeCard, setActiveCard] = useState(0);
    const [actualites, setActualites] = useState([]);
    const carouselRef = useRef(null);
    const dragX = useMotionValue(0);

    useEffect(() => {
        const fetchActualites = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/actualite?populate=*`, {
                    headers: {
                        Authorization: `${process.env.REACT_APP_API_TOKEN}`
                    }
                });
                // Prendre les 6 dernières actualités
                setActualites(response.data.data.slice(0, 6));
            } catch (error) {
                console.error('Error fetching actualites:', error);
            }
        };

        fetchActualites();
    }, []);

    const handleCardChange = (direction) => {
        if (direction === "next") {
            setActiveCard((prev) => (prev === actualites.length - 1 ? 0 : prev + 1));
        } else {
            setActiveCard((prev) => (prev === 0 ? actualites.length - 1 : prev - 1));
        }
    };

    const handleDragEnd = (event, info) => {
        const threshold = 50;
        if (Math.abs(info.offset.x) > threshold) {
            if (info.offset.x > 0) {
                handleCardChange("prev");
            } else {
                handleCardChange("next");
            }
        }
        animate(dragX, 0);
    };

    if (!actualites.length) return null;

    return (
        <section className="relative bg-cream px-4 py-12">
            <h2 className="text-3xl font-cormorant text-center mb-8">Dernières Actualités</h2>

            <div className="relative">
                {/* Navigation buttons */}
                <button
                    onClick={() => handleCardChange("prev")}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-cream text-cocoa rounded-full shadow-lg"
                >
                    ←
                </button>
                <button
                    onClick={() => handleCardChange("next")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-cream text-cocoa rounded-full shadow-lg"
                >
                    →
                </button>

                {/* Navigation dots */}
                <div className="flex justify-center gap-2 mb-6">
                    {actualites.map((_, index) => (
                        <button
                            key={index}
                            className={`w-2 h-2 rounded-full transition-colors ${
                                index === activeCard ? "bg-cocoa" : "bg-cocoa/30"
                            }`}
                            onClick={() => setActiveCard(index)}
                        />
                    ))}
                </div>

                {/* Carousel */}
                <div className="overflow-hidden">
                    <motion.div
                        ref={carouselRef}
                        className="flex"
                        drag="x"
                        dragConstraints={{ left: -((actualites.length - 1) * window.innerWidth), right: 0 }}
                        dragElastic={0.2}
                        onDragEnd={handleDragEnd}
                        animate={{ x: `-${activeCard * 100}%` }}
                        transition={{ type: "spring", damping: 20 }}
                    >
                        {actualites.map((actualite, index) => (
                            <Link 
                                to={`/actualites/${actualite.documentId}`}
                                key={actualite.id}
                                className="w-full flex-shrink-0 px-2"
                            >
                                <motion.div
                                    className="relative h-[400px] rounded-xl overflow-hidden shadow-lg"
                                    animate={{ scale: index === activeCard ? 1 : 0.9 }}
                                >
                                    <div
                                        className="absolute inset-0 bg-cover bg-center"
                                        style={{
                                            backgroundImage: `url(${actualite.imageCouverture.url})`
                                        }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                                    
                                    <div className="absolute inset-0 p-6 flex flex-col justify-between text-white">
                                        <div>
                                            <span className="inline-block px-3 py-1 bg-brandy-rose rounded-full text-sm mb-4">
                                                {actualite.category}
                                            </span>
                                            <h3 className="text-2xl font-cormorant mb-2">{actualite.titre}</h3>
                                            <p className="text-sm line-clamp-3">{actualite.extrait}</p>
                                        </div>

                                        <div className="flex justify-between items-center">
                                            <span className="text-sm opacity-75">
                                                {format(new Date(actualite.datePublication), 'dd MMMM yyyy', { locale: fr })}
                                            </span>
                                            <span className="text-sm font-medium">Lire la suite →</span>
                                        </div>
                                    </div>
                                </motion.div>
                            </Link>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default MobileCarousel;